import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Card } from '../common';
import { Preset, PresetCreateRequest, PresetUpdateRequest, OrderSide, AmountType, OrderType, OrderFlags, TakeProfitSettings, StopLossSettings } from '../../types/preset';

interface PresetFormProps {
  preset?: Preset;
  onSubmit: (preset: PresetCreateRequest | PresetUpdateRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.5rem 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const RadioLabel = styled.label<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  background-color: ${({ isSelected }) => (isSelected ? '#eff6ff' : '#f9fafb')};
  border: 1px solid ${({ isSelected }) => (isSelected ? '#2563eb' : '#d1d5db')};
  color: ${({ isSelected }) => (isSelected ? '#2563eb' : '#4b5563')};
`;

const RadioInput = styled.input`
  margin: 0;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 0.5rem;
`;

const CheckboxInput = styled.input`
  margin: 0;
  width: 1rem;
  height: 1rem;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

const ErrorMessage = styled.p`
  color: #dc2626;
  font-size: 0.875rem;
  margin: 0.5rem 0 0 0;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SymbolOptions = [
  'BTC-USD-PERP', 'ETH-USD-PERP', 'SOL-USD-PERP', 'ARB-USD-PERP', 'LINK-USD-PERP', 'AVAX-USD-PERP', 'MATIC-USD-PERP', 'OP-USD-PERP', 'DOGE-USD-PERP', 'ATOM-USD-PERP'
];

const defaultFlags: OrderFlags = { reduce_only: false, post_only: false };
const defaultTakeProfit: TakeProfitSettings = { enabled: false };
const defaultStopLoss: StopLossSettings = { enabled: false };

const PresetForm: React.FC<PresetFormProps> = ({ 
  preset, 
  onSubmit, 
  onCancel, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState<Partial<PresetCreateRequest | PresetUpdateRequest>>({
    name: preset?.name || '',
    description: preset?.description || '',
    symbol: preset?.symbol || 'BTC-USD-PERP',
    order_type: preset?.order_type || 'market',
    side: preset?.side || 'buy',
    amount: preset?.amount || 0,
    amount_type: preset?.amount_type || 'base',
    leverage: preset?.leverage || 10,
    webhook_enabled: preset?.webhook_enabled ?? false,
    webhook_id: preset?.webhook_id || '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.symbol) newErrors.symbol = 'Symbol is required';
    if (!formData.side) newErrors.side = 'Side (buy/sell) is required';
    if (formData.amount === undefined || formData.amount <= 0) newErrors.amount = 'Amount must be positive';
    if (!formData.amount_type) newErrors.amount_type = 'Amount Type is required';
    if (formData.leverage === undefined || formData.leverage < 1) newErrors.leverage = 'Leverage must be at least 1';
    if (formData.order_type !== 'market') newErrors.order_type = 'Only market orders supported in this simplified form';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: string | number | boolean = value;
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number' || name === 'leverage' || name === 'amount') {
      processedValue = parseFloat(value) || 0; 
    } else if (name === 'side') {
      processedValue = value as OrderSide;
    } else if (name === 'amount_type') {
      processedValue = value as AmountType;
    } else if (name === 'order_type') {
      processedValue = value as OrderType;
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const requestData = { ...formData } as Partial<PresetCreateRequest | PresetUpdateRequest>; 

      requestData.order_type = 'market';
      requestData.side = formData.side as OrderSide;
      requestData.amount = Number(formData.amount); 
      requestData.amount_type = formData.amount_type as AmountType;
      requestData.leverage = Number(formData.leverage);
      requestData.symbol = formData.symbol || '';
      requestData.name = formData.name || '';
      requestData.preset_type = 'preset';
      requestData.take_profit = formData.take_profit || defaultTakeProfit;
      requestData.stop_loss = formData.stop_loss || defaultStopLoss;
      requestData.flags = formData.flags || defaultFlags;
      requestData.description = formData.description || null;
      requestData.limit_price = formData.limit_price || null;
      requestData.limit_price_type = formData.limit_price_type || null;
      requestData.stop_price = formData.stop_price || null;
      requestData.layered = formData.layered || null;
      requestData.webhook_enabled = formData.webhook_enabled || false;
      requestData.webhook_id = formData.webhook_id || null;
      requestData.margin_mode = formData.margin_mode || 'cross';

      Object.keys(requestData).forEach((key) => {
        const K = key as keyof typeof requestData;
        if (requestData[K] === undefined) {
          delete requestData[K];
        }
      });

      try {
        await onSubmit(requestData as PresetCreateRequest | PresetUpdateRequest);
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors({ form: 'Failed to submit preset. Please check console.' });
      }
    }
  };
  
  return (
    <Card>
      <FormContainer onSubmit={handleSubmit}>
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          <FormField>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter preset name"
              hasError={!!errors.name}
              fullWidth
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormField>
          
          <FormField>
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              name="description"
              value={String(formData.description ?? '')}
              onChange={handleChange}
              placeholder="Enter preset description"
              hasError={!!errors.description}
              fullWidth
            />
          </FormField>
        </FormSection>
        
        <FormSection>
          <SectionTitle>Order Details (Market Order Only for MVP Test)</SectionTitle>
          
          <div>
            <label htmlFor="symbol">Symbol</label>
            <select
              id="symbol"
              name="symbol"
              value={formData.symbol}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.5rem',
                marginTop: '0.25rem',
                borderRadius: '0.375rem',
                border: errors.symbol ? '1px solid #dc2626' : '1px solid #d1d5db'
              }}
            >
              {SymbolOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {errors.symbol && <ErrorMessage>{errors.symbol}</ErrorMessage>}
          </div>

          <input type="hidden" name="order_type" value="market" />
          {errors.order_type && <ErrorMessage>{errors.order_type}</ErrorMessage>}
          
          <div>
            <label htmlFor="side">Side</label>
            <RadioGroup>
              <RadioLabel isSelected={formData.side === 'buy'}>
                <RadioInput
                  type="radio"
                  name="side"
                  value="buy"
                  checked={formData.side === 'buy'}
                  onChange={handleChange}
                  id="buy-side"
                />
                Buy / Long
              </RadioLabel>
              <RadioLabel isSelected={formData.side === 'sell'}>
                <RadioInput
                  type="radio"
                  name="side"
                  value="sell"
                  checked={formData.side === 'sell'}
                  onChange={handleChange}
                  id="sell-side"
                />
                Sell / Short
              </RadioLabel>
            </RadioGroup>
            {errors.side && <ErrorMessage>{errors.side}</ErrorMessage>}
          </div>
          
          <FormRow>
            <div style={{ flex: 1 }}>
              <FormField>
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  step="any"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter order amount"
                  hasError={!!errors.amount}
                />
                {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
              </FormField>
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="amount_type">Amount Type</label>
              <select 
                id="amount_type" 
                name="amount_type" 
                value={formData.amount_type}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  marginTop: '0.25rem',
                  borderRadius: '0.375rem',
                  border: errors.amount_type ? '1px solid #dc2626' : '1px solid #d1d5db'
                }}
              >
                <option value="base">Base</option>
                <option value="quote">Quote</option>
                <option value="percent">Percent</option>
              </select>
              {errors.amount_type && <ErrorMessage>{errors.amount_type}</ErrorMessage>}
            </div>
          </FormRow>

          <FormField>
            <Label htmlFor="leverage">Leverage</Label>
            <Input
              id="leverage"
              name="leverage"
              type="number"
              step="1"
              min="1"
              value={formData.leverage}
              onChange={handleChange}
              placeholder="Enter leverage (e.g., 10)"
              hasError={!!errors.leverage}
            />
            {errors.leverage && <ErrorMessage>{errors.leverage}</ErrorMessage>}
          </FormField>
        </FormSection>
        
        <FormSection>
          <SectionTitle>Webhook Configuration</SectionTitle>
          <CheckboxLabel>
            <CheckboxInput 
              type="checkbox" 
              name="webhook_enabled"
              checked={!!formData.webhook_enabled}
              onChange={handleChange}
            />
            Enable Webhook Trigger
          </CheckboxLabel>
          {formData.webhook_enabled && (
            <FormField>
              <Label htmlFor="webhook_id">Webhook ID (Used in TradingView Alert)</Label>
              <Input
                id="webhook_id"
                name="webhook_id"
                value={formData.webhook_id || ''}
                onChange={handleChange}
                placeholder="Enter unique webhook ID (e.g., my_btc_market_buy)"
                hasError={!!errors.webhook_id}
                fullWidth
              />
              {errors.webhook_id && <ErrorMessage>{errors.webhook_id}</ErrorMessage>}
            </FormField>
          )}
        </FormSection>
        
        {errors.form && <ErrorMessage>{errors.form}</ErrorMessage>}

        <FormActions>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : (preset ? 'Update Preset' : 'Create Preset')}
          </Button>
        </FormActions>
      </FormContainer>
    </Card>
  );
};

export default PresetForm; 